/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { useMemo } from 'react';
import { Table } from 'antd';
import { useIntl } from 'react-intl';
import { DbQuestion, DbAnswer } from '~src/types/db';
import { formatDate } from '~src/utils/formatDate';
import { DB_STATE } from '~src/types/enums';

type QATableProps = {
  questions: DbQuestion[];
  anonymousMode: boolean;
  isVendorSide: boolean;
};

type TableRow = {
  key: string;
  question: string;
  questionDate: string;
  author: string;
  answer: string;
  answerDate: string;
  answerAuthor: string;
};

const QATable: React.FC<QATableProps> = ({
  questions,
  anonymousMode,
  isVendorSide,
}) => {
  const intl = useIntl();

  // Flatten questions and answers into table rows
  const tableData = useMemo<TableRow[]>(() => {
    const rows: TableRow[] = [];

    questions.forEach((question) => {
      // Filter valid answers (exclude rejected and removed)
      const validAnswers = (question.answers || []).filter(
        (a) => a.state !== DB_STATE.REJECTED && a.state !== DB_STATE.REMOVED
      );

      const questionText = question.question || '';
      const questionDate = formatDate(question.created_at, 'long');
      const questionAuthor = getAuthorDisplay(
        question.created_by,
        anonymousMode || isVendorSide,
        intl
      );

      if (validAnswers.length === 0) {
        // Question with no answers
        rows.push({
          key: `q-${question.id}`,
          question: questionText,
          questionDate: questionDate,
          author: questionAuthor,
          answer: '',
          answerDate: '',
          answerAuthor: '',
        });
      } else {
        // Question with answers - create a row for each answer
        validAnswers.forEach((answer, index) => {
          rows.push({
            key: `q-${question.id}-a-${answer.id}`,
            question: index === 0 ? questionText : '',
            questionDate: index === 0 ? questionDate : '',
            author: index === 0 ? questionAuthor : '',
            answer: answer.answer || '',
            answerDate: formatDate(answer.created_at, 'long'),
            answerAuthor: getAuthorDisplay(
              answer.created_by,
              anonymousMode || isVendorSide,
              intl
            ),
          });
        });
      }
    });

    return rows;
  }, [questions, anonymousMode, isVendorSide, intl]);

  // Define columns based on anonymous mode
  const columns = useMemo(() => {
    if (anonymousMode || isVendorSide) {
      // Anonymous mode: 4 columns
      return [
        {
          title: intl.formatMessage({ id: 'qa-table-question' }),
          dataIndex: 'question',
          key: 'question',
          width: '35%',
          className: 'qa-table-cell',
          render: (text: string) => (
            <div style={{ whiteSpace: 'pre-line', wordBreak: 'break-word' }}>
              {text}
            </div>
          ),
        },
        {
          title: intl.formatMessage({ id: 'qa-table-question-date' }),
          dataIndex: 'questionDate',
          key: 'questionDate',
          width: '15%',
          className: 'qa-table-cell',
        },
        {
          title: intl.formatMessage({ id: 'qa-table-answer' }),
          dataIndex: 'answer',
          key: 'answer',
          width: '35%',
          className: 'qa-table-cell',
          render: (text: string) => (
            <div style={{ whiteSpace: 'pre-line', wordBreak: 'break-word' }}>
              {text}
            </div>
          ),
        },
        {
          title: intl.formatMessage({ id: 'qa-table-answer-date' }),
          dataIndex: 'answerDate',
          key: 'answerDate',
          width: '15%',
          className: 'qa-table-cell',
        },
      ];
    } else {
      // Non-anonymous mode: 6 columns
      // Order: Question | Author | Question Date | Answer | Answer Author | Answer Date
      return [
        {
          title: intl.formatMessage({ id: 'qa-table-question' }),
          dataIndex: 'question',
          key: 'question',
          width: '25%',
          className: 'qa-table-cell',
          render: (text: string) => (
            <div style={{ whiteSpace: 'pre-line', wordBreak: 'break-word' }}>
              {text}
            </div>
          ),
        },
        {
          title: intl.formatMessage({ id: 'qa-table-author' }),
          dataIndex: 'author',
          key: 'author',
          width: '13%',
          className: 'qa-table-cell',
          render: (text: string) => (
            <div style={{ whiteSpace: 'pre-line', fontSize: '13px' }}>
              {text}
            </div>
          ),
        },
        {
          title: intl.formatMessage({ id: 'qa-table-question-date' }),
          dataIndex: 'questionDate',
          key: 'questionDate',
          width: '12%',
          className: 'qa-table-cell',
        },
        {
          title: intl.formatMessage({ id: 'qa-table-answer' }),
          dataIndex: 'answer',
          key: 'answer',
          width: '25%',
          className: 'qa-table-cell',
          render: (text: string) => (
            <div style={{ whiteSpace: 'pre-line', wordBreak: 'break-word' }}>
              {text}
            </div>
          ),
        },
        {
          title: intl.formatMessage({ id: 'qa-table-answer-author' }),
          dataIndex: 'answerAuthor',
          key: 'answerAuthor',
          width: '13%',
          className: 'qa-table-cell',
          render: (text: string) => (
            <div style={{ whiteSpace: 'pre-line', fontSize: '13px' }}>
              {text}
            </div>
          ),
        },
        {
          title: intl.formatMessage({ id: 'qa-table-answer-date' }),
          dataIndex: 'answerDate',
          key: 'answerDate',
          width: '12%',
          className: 'qa-table-cell',
        },
      ];
    }
  }, [anonymousMode, isVendorSide, intl]);

  return (
    <div>
      <Table
        dataSource={tableData}
        columns={columns}
        pagination={false}
        bordered
        size="small"
        rowClassName={(_, index) =>
          index % 2 === 0 ? 'qa-table-row-even' : 'qa-table-row-odd'
        }
      />

      <style jsx global>{`
        .qa-table-cell {
          font-size: 14px;
          padding: 12px 8px !important;
        }

        .qa-table-row-even {
          background-color: #fafafa;
        }

        .qa-table-row-odd {
          background-color: #ffffff;
        }

        @media print {
          .qa-table-cell {
            padding: 8px 6px !important;
            font-size: 12px;
          }

          .ant-table {
            font-size: 12px;
          }

          .ant-table-thead > tr > th {
            background: #f0f0f0 !important;
            font-weight: bold;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }

          .qa-table-row-even {
            background-color: #fafafa !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }

          .qa-table-row-odd {
            background-color: #ffffff !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
        }
      `}</style>
    </div>
  );
};

// Helper function to get author display based on anonymization
const getAuthorDisplay = (
  author: any,
  anonymous: boolean,
  intl: any
): string => {
  if (anonymous) {
    return intl.formatMessage({ id: 'qa-vendor-anonymous' });
  }

  if (!author) {
    return intl.formatMessage({
      id: 'qa-question-author-not-found',
      defaultMessage: 'Unknown',
    });
  }

  // Format: "Full Name | email@example.com | Company Name"
  const parts: string[] = [];

  if (author.full_name) {
    parts.push(author.full_name);
  }

  if (author.email) {
    parts.push(author.email);
  }

  if (author.company_name) {
    parts.push(author.company_name);
  }

  return parts.length > 0
    ? parts.join(' | ')
    : intl.formatMessage({
        id: 'qa-question-author-not-found',
        defaultMessage: 'Unknown',
      });
};

export default QATable;
